import React, { useEffect, useMemo, useState } from "react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import toast from "react-hot-toast";
import { facilityAdmin } from "../../api";
import BRAND from "../../brand";
import Icon from "../../components/Icons";
import ExportMenu from "./ExportMenu";

// Redesigned Daily tab for white-label builds: filters, KPI cards with "vs previous day",
// patient flow, queue status, wait by station, peak arrivals, efficiency and a station table.
// All numbers come from /reports/daily-overview/ (same definitions as the classic Daily tab).

const P = BRAND.colorPrimary;
const A = BRAND.colorAccent;
const GOOD = "#16A34A";
const BAD = "#DC2626";
const WARN = "#D97706";

const hourLabel = (h) => `${h % 12 === 0 ? 12 : h % 12} ${h < 12 ? "AM" : "PM"}`;
const mins = (v) => (v == null ? "–" : `${v % 1 === 0 ? v : v.toFixed(1)} min`);

const TYPES = [
  ["all", "All ticket types"], ["normal", "Normal"], ["priority", "Priority"],
  ["emergency", "Emergency"], ["appointment", "Appointment"],
];

// "▲ +12% vs previous day": colour says whether the change is good news
function Change({ cur, prev, lowerIsBetter = false, unit = "pct" }) {
  if (cur == null || prev == null) return <span style={S.mut}>no previous-day data</span>;
  const diff = cur - prev;
  if (diff === 0) return <span style={S.mut}>same as previous day</span>;
  let text;
  if (unit === "min") text = `${diff > 0 ? "+" : "−"}${Math.abs(Math.round(diff * 10) / 10)} min`;
  else if (prev === 0) return <span style={S.mut}>previous day: 0</span>;
  else text = `${diff > 0 ? "+" : "−"}${Math.abs(Math.round((diff / prev) * 100))}%`;
  const good = lowerIsBetter ? diff < 0 : diff > 0;
  return <span style={{ color: good ? GOOD : BAD, fontWeight: 600 }}>{diff > 0 ? "▲" : "▼"} {text} <span style={S.mut}>vs previous day</span></span>;
}

function Kpi({ icon, tint, label, value, children }) {
  return (
    <div style={{ ...S.card, ...S.kpi, borderTop: `3px solid ${tint}` }}>
      <div style={S.kpiHead}>
        <span style={{ ...S.bubble, background: `${tint}22`, color: tint }}><Icon name={icon} size={16} /></span>
        <span style={S.kpiLabel}>{label}</span>
      </div>
      <div style={S.kpiValue}>{value}</div>
      <div style={S.kpiFoot}>{children}</div>
    </div>
  );
}

const Card = ({ title, right, children, style }) => (
  <div style={{ ...S.card, ...style }}>
    <div style={S.cardHead}><span style={S.cardTitle}>{title}</span>{right}</div>
    {children}
  </div>
);

export default function DailyOverview({ date }) {
  const [zone, setZone] = useState("");
  const [station, setStation] = useState("");
  const [type, setType] = useState("all");
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    facilityAdmin.dailyOverview({ date, zone: zone || undefined, station: station || undefined, ticket_type: type })
      .then((r) => { if (live) setD(r.data); })
      .catch(() => { if (live) toast.error("Failed to load the daily overview."); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [date, zone, station, type]);

  const flow = useMemo(() => {
    if (!d) return [];
    const hours = [...d.flow.issued.keys()].filter((h) => d.flow.issued[h] || d.flow.served[h]);
    if (!hours.length) return [];
    const from = Math.max(0, Math.min(...hours) - 1), to = Math.min(23, Math.max(...hours) + 1);
    return Array.from({ length: to - from + 1 }, (_, i) => ({ hour: hourLabel(from + i), issued: d.flow.issued[from + i], served: d.flow.served[from + i] }));
  }, [d]);

  if (!d) return loading ? <div style={S.mut}>Loading…</div> : null;
  const k = d.kpis;
  const st = d.status;
  const donut = [
    { name: "Served", value: st.served, color: GOOD },
    { name: "Waiting", value: st.waiting, color: P },
    { name: "Abandoned", value: st.abandoned, color: BAD },
    ...(st.expired ? [{ name: "Expired", value: st.expired, color: WARN }] : []),
  ].filter((x) => x.value > 0);
  const zoneStations = d.filters.stations.filter((s) => !zone || s.zone_id === zone);
  const maxWait = Math.max(1, ...d.stations.map((r) => r.avg_wait || 0));
  const peakData = d.flow.issued.map((n, h) => ({ hour: hourLabel(h), n, h })).filter((x) => x.n || (x.h >= 7 && x.h <= 18));
  const peakHours = new Set(d.peaks.map((p) => p.hour));

  return (
    <div style={{ ...S.col, opacity: loading ? 0.6 : 1 }} className="eq-overview">
      <div style={S.filters}>
        <select style={S.select} value={zone} onChange={(e) => { setZone(e.target.value); setStation(""); }}>
          <option value="">Zone: All zones</option>
          {d.filters.zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
        </select>
        <select style={S.select} value={station} onChange={(e) => setStation(e.target.value)}>
          <option value="">Station: All stations</option>
          {zoneStations.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select style={S.select} value={type} onChange={(e) => setType(e.target.value)}>
          {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <ExportMenu style={{ marginLeft: "auto" }} report="overview" params={{ date, zone, station, ticket_type: type }} />
      </div>

      <div style={S.kpiRow}>
        <Kpi icon="ticket" tint={P} label="Tickets Issued" value={k.issued.value}><Change cur={k.issued.value} prev={k.issued.previous} /></Kpi>
        <Kpi icon="users" tint={GOOD} label="Patients Served" value={k.served.value}><Change cur={k.served.value} prev={k.served.previous} /></Kpi>
        <Kpi icon="hourglass" tint={WARN} label="Currently Waiting" value={k.waiting.value}><span style={S.mut}>right now</span></Kpi>
        <Kpi icon="x" tint={BAD} label="Abandoned" value={k.abandoned.value}><Change cur={k.abandoned.value} prev={k.abandoned.previous} lowerIsBetter /></Kpi>
        <Kpi icon="clock" tint={A} label="Avg. Wait Time" value={mins(k.avg_wait.value)}><Change cur={k.avg_wait.value} prev={k.avg_wait.previous} lowerIsBetter unit="min" /></Kpi>
        <Kpi icon="signal" tint={P} label="Avg. Service Time" value={mins(k.avg_service.value)}><Change cur={k.avg_service.value} prev={k.avg_service.previous} lowerIsBetter unit="min" /></Kpi>
      </div>

      <div style={S.row2}>
        <Card title="Patient flow throughout the day">
          {flow.length === 0 ? <div style={S.empty}>No tickets issued on this day.</div> : (
            <div style={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={flow} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
                  <CartesianGrid stroke="var(--surface-border)" vertical={false} />
                  <XAxis dataKey="hour" tick={{ fontSize: 11, fill: "var(--text-secondary)" }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "var(--text-secondary)" }} />
                  <Tooltip />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                  <Area type="monotone" dataKey="issued" name="Tickets issued" stroke={A} fill={`${A}33`} strokeWidth={2} />
                  <Area type="monotone" dataKey="served" name="Patients served" stroke={P} fill={`${P}33`} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
        <Card title="Queue status">
          {donut.length === 0 ? <div style={S.empty}>Nothing to show yet.</div> : (
            <div style={S.donutWrap}>
              <div style={{ width: 170, height: 170, position: "relative" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart><Pie data={donut} dataKey="value" innerRadius={52} outerRadius={80} paddingAngle={2} stroke="none">
                    {donut.map((x) => <Cell key={x.name} fill={x.color} />)}
                  </Pie></PieChart>
                </ResponsiveContainer>
                <div style={S.donutMid}><b style={{ fontSize: 26 }}>{st.total}</b><span style={S.mut}>Tickets</span></div>
              </div>
              <div style={S.legend}>
                {donut.map((x) => (
                  <div key={x.name} style={S.legendRow}>
                    <span style={{ ...S.dot, background: x.color }} />{x.name}
                    <b style={{ marginLeft: "auto" }}>{x.value}</b><span style={S.mut}>({Math.round((x.value / st.total) * 100)}%)</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>

      <div style={S.row3}>
        <Card title="Wait time by station">
          {d.stations.length === 0 ? <div style={S.empty}>No stations.</div> : d.stations.map((r) => (
            <div key={r.station_id} style={S.barRow}>
              <span style={S.barName}>{r.station_name}</span>
              <div style={S.track}><div style={{ ...S.fill, width: `${((r.avg_wait || 0) / maxWait) * 100}%`, background: A }} /></div>
              <span style={S.barVal}>{mins(r.avg_wait)}</span>
            </div>
          ))}
        </Card>
        <Card title="Peak arrival periods" right={d.peaks.length ? <span style={S.pill}>Peaks: {d.peaks.map((p) => p.label).join(", ")}</span> : null}>
          <div style={{ height: 170 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={peakData} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
                <XAxis dataKey="hour" tick={{ fontSize: 10, fill: "var(--text-secondary)" }} interval={0} angle={-30} textAnchor="end" height={42} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "var(--text-secondary)" }} />
                <Tooltip />
                <Bar dataKey="n" name="Tickets" radius={[3, 3, 0, 0]}>
                  {peakData.map((x) => <Cell key={x.h} fill={peakHours.has(x.h) ? P : `${P}66`} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="Service efficiency">
          <div style={S.eff}>
            <div style={S.effRow}><span style={{ ...S.effBig, color: GOOD }}>{d.efficiency.completion_rate}%</span><div><div>Completion rate</div><Change cur={d.efficiency.completion_rate} prev={d.efficiency.completion_rate_previous} /></div></div>
            <div style={S.effRow}><span style={{ ...S.effBig, color: P }}>{mins(d.efficiency.avg_service)}</span><div><div>Average service time</div><Change cur={d.efficiency.avg_service} prev={d.efficiency.avg_service_previous} lowerIsBetter unit="min" /></div></div>
            {d.efficiency.within_target != null && (
              <div style={S.effRow}><span style={{ ...S.effBig, color: A }}>{d.efficiency.within_target}%</span><div><div>Within target wait</div><span style={S.mut}>against each station's own target</span></div></div>
            )}
          </div>
        </Card>
      </div>

      <Card title="Station performance breakdown">
        <table style={S.table}>
          <thead><tr>{["Station", "Tickets", "Served", "Avg wait", "Avg service", "Completion"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
          <tbody>
            {d.stations.map((r) => (
              <tr key={r.station_id}>
                <td style={S.td}><b>{r.station_name}</b><div style={S.mut}>{r.zone_name}</div></td>
                <td style={S.td}>{r.tickets}</td><td style={S.td}>{r.served}</td>
                <td style={S.td}>{mins(r.avg_wait)}</td><td style={S.td}>{mins(r.avg_service)}</td>
                <td style={S.td}><span style={{ ...S.rate, background: r.completion_rate >= 90 ? `${GOOD}22` : `${WARN}22`, color: r.completion_rate >= 90 ? GOOD : WARN }}>{r.completion_rate}%</span></td>
              </tr>
            ))}
          </tbody>
        </table>
        {d.notes.length > 0 && (
          <div style={S.insight}><b>Noted:</b> {d.notes.join(" ")}</div>
        )}
      </Card>
    </div>
  );
}

const S = {
  col: { display: "flex", flexDirection: "column", gap: 16 },
  mut: { color: "var(--text-secondary)", fontSize: 12 },
  empty: { color: "var(--text-secondary)", fontSize: 13, padding: "30px 0", textAlign: "center" },
  filters: { display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" },
  select: { background: "var(--input-bg)", border: "1px solid var(--input-border)", color: "var(--text-primary)", borderRadius: 10, padding: "9px 12px", fontSize: 13, fontFamily: "inherit" },
  exportBtn: { marginLeft: "auto", background: P, color: "#fff", border: "none", borderRadius: 10, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  card: { background: "var(--surface-bg)", border: "1px solid var(--surface-border)", borderRadius: 14, padding: "16px 18px", boxShadow: "var(--card-shadow)" },
  cardHead: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, gap: 8 },
  cardTitle: { fontSize: 14, fontWeight: 700, color: "var(--text-primary)" },
  kpiRow: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12 },
  kpi: { padding: "14px 16px" },
  kpiHead: { display: "flex", alignItems: "center", gap: 8 },
  bubble: { width: 28, height: 28, borderRadius: 8, display: "inline-flex", alignItems: "center", justifyContent: "center" },
  kpiLabel: { fontSize: 12, fontWeight: 600, color: "var(--text-secondary)" },
  kpiValue: { fontSize: 30, fontWeight: 700, color: "var(--text-primary)", margin: "6px 0 2px", letterSpacing: "-0.5px" },
  kpiFoot: { fontSize: 12, minHeight: 16 },
  row2: { display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)", gap: 16 },
  row3: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 },
  donutWrap: { display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap", justifyContent: "center" },
  donutMid: { position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "var(--text-primary)" },
  legend: { flex: 1, minWidth: 140, display: "flex", flexDirection: "column", gap: 10, fontSize: 13, color: "var(--text-primary)" },
  legendRow: { display: "flex", alignItems: "center", gap: 8 },
  dot: { width: 9, height: 9, borderRadius: "50%", display: "inline-block" },
  barRow: { display: "flex", alignItems: "center", gap: 10, margin: "9px 0", fontSize: 13, color: "var(--text-primary)" },
  barName: { width: 110, flexShrink: 0 },
  track: { flex: 1, height: 10, background: "var(--surface-border)", borderRadius: 6, overflow: "hidden" },
  fill: { height: "100%", borderRadius: 6 },
  barVal: { width: 64, textAlign: "right", fontWeight: 600 },
  pill: { fontSize: 11, background: `${P}22`, color: P, borderRadius: 20, padding: "3px 10px", fontWeight: 600 },
  eff: { display: "flex", flexDirection: "column", gap: 14, color: "var(--text-primary)", fontSize: 13 },
  effRow: { display: "flex", alignItems: "center", gap: 14 },
  effBig: { fontSize: 22, fontWeight: 700, minWidth: 84 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13, color: "var(--text-primary)" },
  th: { textAlign: "left", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-secondary)", padding: "8px 10px", borderBottom: "1px solid var(--surface-border)" },
  td: { padding: "10px", borderBottom: "1px solid var(--surface-border)" },
  rate: { borderRadius: 20, padding: "3px 10px", fontWeight: 700, fontSize: 12 },
  insight: { marginTop: 14, background: `${A}1F`, borderRadius: 10, padding: "10px 14px", fontSize: 13, color: "var(--text-primary)" },
};
